import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react'
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  Position,
  Handle,
  NodeProps,
  type NodeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Divider,
  Stack,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import KitchenIcon from '@mui/icons-material/Countertops'
import LineIcon from '@mui/icons-material/PrecisionManufacturing'
import CleanIcon from '@mui/icons-material/CleaningServices'
import ProductIcon from '@mui/icons-material/Inventory2'
import LocationIcon from '@mui/icons-material/LocationOn'
import SpeedIcon from '@mui/icons-material/Speed'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SyncIcon from '@mui/icons-material/Sync'
import PeopleIcon from '@mui/icons-material/People'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ScheduleIcon from '@mui/icons-material/Schedule'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import SaveIcon from '@mui/icons-material/Save'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ScienceIcon from '@mui/icons-material/Science'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {
  SchedulerPackagingRoutesRowType,
  WorkstationDownTimeRowType,
  ProductionRequirementRowType,
  SynchronizedLinesRowType,
  SchedulerCleningMappingRowType,
} from './Scheduler.types'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { MiniGantt } from './ScheduleGantt.view'
import { ScheduleControlPanel } from './ScheduleControlPanel.view'

// ─── Types ───────────────────────────────────────────────────────────────────

type KitchenData = {
  name: string
  baseMaterials: {
    code: string
    name: string
    rate: number
    products: { code: string; name: string }[]
  }[]
  downtime: string | null
}

type LineData = {
  name: string
  products: {
    code: string
    name: string
    rate: number
    pr: number
  }[]
  downtime: string | null
  site: string
  syncGroupCount: number
}

type CipData = {
  name: string
  assets: string[]
  downtime: string | null
}

type FocusedItem = {
  type: 'product' | 'baseMaterial'
  code: string
} | null

// ─── Product Flow Custom Nodes ──────────────────────────────────────────────

const FlowBaseMaterialNode = memo(({ data }: NodeProps) => {
  const d = data as unknown as {
    code: string
    name: string
    kitchenRate: string
    onChangeKitchenRate?: (val: string) => void
  }
  const [rate, setRate] = useState(d.kitchenRate || '')
  return (
    <Paper
      elevation={2}
      sx={{
        p: 2.5,
        minWidth: 280,
        borderLeft: '6px solid #7b1fa2',
        borderRadius: 2,
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#7b1fa2', width: 12, height: 12 }}
      />
      <Typography
        sx={{
          fontSize: '0.85rem',
          color: 'text.secondary',
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: 0.5,
        }}
      >
        Base Material
      </Typography>
      <Typography sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
        {d.code}
      </Typography>
      {d.name && (
        <Typography
          sx={{ fontSize: '0.95rem', color: 'text.secondary', mb: 0.75 }}
          noWrap
        >
          {d.name}
        </Typography>
      )}
      {d.onChangeKitchenRate ? (
        <Box className="nodrag nowheel" sx={{ mt: 0.75 }}>
          <EditableField
            label="Kitchen Rate"
            value={rate}
            unit="u/hr"
            onChange={(v) => {
              setRate(v)
              d.onChangeKitchenRate!(v)
            }}
          />
        </Box>
      ) : (
        parseFloat(d.kitchenRate) > 0 && (
          <Chip
            icon={<SpeedIcon sx={{ fontSize: '14px !important' }} />}
            label={`${d.kitchenRate} u/hr`}
            size="small"
            variant="outlined"
            sx={{ height: 26, fontSize: '0.85rem', mt: 0.75 }}
          />
        )
      )}
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: '#7b1fa2', width: 12, height: 12 }}
      />
    </Paper>
  )
})

const FlowProductNode = memo(({ data }: NodeProps) => {
  const d = data as unknown as {
    code: string
    name: string
    quantity: string
    conversionFactor: string
    requiredTemperature: number | null
    dontStartBefore: number | null
    finishBefore: number | null
    onChangeReq?: (field: string, val: string) => void
  }
  const [qty, setQty] = useState(d.quantity || '')
  const [cf, setCf] = useState(d.conversionFactor || '')
  const [temp, setTemp] = useState<string>(
    d.requiredTemperature != null ? String(d.requiredTemperature) : ''
  )
  const [startAfter, setStartAfter] = useState(
    d.dontStartBefore != null ? String(d.dontStartBefore) : ''
  )
  const [finishBy, setFinishBy] = useState(
    d.finishBefore != null ? String(d.finishBefore) : ''
  )

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2.5,
        minWidth: 380,
        borderLeft: '6px solid #0288d1',
        borderRadius: 2,
        bgcolor: '#f5faff',
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#0288d1', width: 12, height: 12 }}
      />
      <Typography
        sx={{
          fontSize: '0.85rem',
          color: 'text.secondary',
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: 0.5,
        }}
      >
        Product
      </Typography>
      <Typography sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
        {d.code}
      </Typography>
      {d.name && (
        <Typography
          sx={{ fontSize: '0.95rem', color: 'text.secondary', mb: 0.75 }}
          noWrap
        >
          {d.name}
        </Typography>
      )}
      <Divider sx={{ my: 1 }} />
      {d.onChangeReq ? (
        <Box className="nodrag nowheel">
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <EditableField
              label="Quantity"
              value={qty}
              onChange={(v) => {
                setQty(v)
                d.onChangeReq!('quantity', v)
              }}
            />
            <EditableField
              label="Conv. Factor"
              value={cf}
              onChange={(v) => {
                setCf(v)
                d.onChangeReq!('conversionFactor', v)
              }}
            />
            <DropdownField
              label="Temp"
              value={temp}
              options={[
                { value: 132, label: '132°C' },
                { value: 135, label: '135°C' },
              ]}
              onChange={(v) => {
                setTemp(v)
                d.onChangeReq!('requiredTemperature', v)
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <EditableField
              label="Start After"
              value={startAfter}
              unit="h"
              onChange={(v) => {
                setStartAfter(v)
                d.onChangeReq!('dontStartBefore', v)
              }}
            />
            <EditableField
              label="Finish By"
              value={finishBy}
              unit="h"
              onChange={(v) => {
                setFinishBy(v)
                d.onChangeReq!('finishBefore', v)
              }}
            />
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {d.quantity && (
              <Chip
                label={`Qty: ${d.quantity}`}
                size="small"
                sx={{ height: 26, fontSize: '0.85rem', bgcolor: '#e3f2fd' }}
              />
            )}
            {d.conversionFactor && (
              <Chip
                label={`CF: ${d.conversionFactor}`}
                size="small"
                sx={{ height: 26, fontSize: '0.85rem' }}
                variant="outlined"
              />
            )}
            {d.requiredTemperature && (
              <Chip
                label={`${d.requiredTemperature}°C`}
                size="small"
                sx={{ height: 26, fontSize: '0.85rem' }}
                variant="outlined"
              />
            )}
          </Box>
          {(d.dontStartBefore != null || d.finishBefore != null) && (
            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
              {d.dontStartBefore != null && (
                <Chip
                  label={`Start ≥${d.dontStartBefore}h`}
                  size="small"
                  sx={{ height: 26, fontSize: '0.85rem' }}
                  variant="outlined"
                />
              )}
              {d.finishBefore != null && (
                <Chip
                  label={`End ≤${d.finishBefore}h`}
                  size="small"
                  sx={{ height: 26, fontSize: '0.85rem' }}
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </>
      )}
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: '#0288d1', width: 12, height: 12 }}
      />
    </Paper>
  )
})

const FlowLineNode = memo(({ data }: NodeProps) => {
  const d = data as unknown as {
    name: string
    runRate: string
    pr: string
    site: string
    assigned: boolean
    onChangeRunRate?: (val: string) => void
  }
  const [rate, setRate] = useState(d.runRate || '')
  const isAssigned = d.assigned || (rate !== '' && rate !== '0')
  return (
    <Paper
      elevation={isAssigned ? 2 : 0}
      sx={{
        p: 2.5,
        minWidth: 260,
        borderLeft: isAssigned ? '6px solid #2e7d32' : '3px dashed #bdbdbd',
        borderRadius: 2,
        bgcolor: isAssigned ? undefined : '#fafafa',
        opacity: isAssigned ? 1 : 0.6,
        transition: 'all 0.2s ease',
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: isAssigned ? '#2e7d32' : '#bdbdbd',
          width: 12,
          height: 12,
        }}
      />
      <Typography
        sx={{
          fontSize: '0.85rem',
          color: 'text.secondary',
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: 0.5,
        }}
      >
        {isAssigned ? 'Packing Line' : 'Available Line'}
      </Typography>
      <Typography
        sx={{
          fontSize: '1.2rem',
          fontWeight: 700,
          color: isAssigned ? 'text.primary' : 'text.secondary',
        }}
      >
        {d.name}
      </Typography>
      {d.site && (
        <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
          {d.site}
        </Typography>
      )}
      <Divider sx={{ my: 1 }} />
      {d.onChangeRunRate ? (
        <Box className="nodrag nowheel" sx={{ display: 'flex', gap: 0.75 }}>
          <EditableField
            label="Run Rate"
            value={rate}
            unit="u/hr"
            onChange={(v) => {
              setRate(v)
              d.onChangeRunRate!(v)
            }}
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {parseFloat(d.runRate) > 0 && (
            <Chip
              icon={<SpeedIcon sx={{ fontSize: '14px !important' }} />}
              label={`${d.runRate} u/hr`}
              size="small"
              variant="outlined"
              sx={{ height: 26, fontSize: '0.85rem' }}
            />
          )}
          {d.pr && (
            <Chip
              label={`PR: ${d.pr}`}
              size="small"
              variant="outlined"
              sx={{ height: 26, fontSize: '0.85rem' }}
            />
          )}
        </Box>
      )}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: isAssigned ? '#2e7d32' : '#bdbdbd',
          width: 12,
          height: 12,
        }}
      />
    </Paper>
  )
})

const FlowKitchenNode = memo(({ data }: NodeProps) => {
  const d = data as unknown as { name: string }
  return (
    <Paper
      elevation={2}
      sx={{
        p: 2.5,
        minWidth: 240,
        borderLeft: '6px solid #1976d2',
        borderRadius: 2,
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#1976d2', width: 12, height: 12 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <KitchenIcon sx={{ color: '#1976d2', fontSize: 28 }} />
        <Box>
          <Typography
            sx={{
              fontSize: '0.85rem',
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontWeight: 600,
              letterSpacing: 0.5,
            }}
          >
            Kitchen
          </Typography>
          <Typography sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
            {d.name}
          </Typography>
        </Box>
      </Box>
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: '#1976d2', width: 12, height: 12 }}
      />
    </Paper>
  )
})

const focusNodeTypes: NodeTypes = {
  flowKitchen: FlowKitchenNode,
  flowBaseMaterial: FlowBaseMaterialNode,
  flowProduct: FlowProductNode,
  flowLine: FlowLineNode,
}

// ─── Product Flow Diagram Builder ───────────────────────────────────────────

const buildProductFlowGraph = (
  productCode: string,
  packagingRoutes: SchedulerPackagingRoutesRowType[],
  productionRequirements: ProductionRequirementRowType[],
  downtimeData: WorkstationDownTimeRowType[],
  allLines: Map<string, LineData>,
  selectedWeek: number,
  onChangeRoute: (field: string, val: string) => void,
  onChangeReq: (field: string, val: string) => void,
  onChangeLineRate: (lineName: string, val: string) => void
) => {
  const routes = packagingRoutes.filter((r) => r.productCode === productCode)
  if (routes.length === 0) return { nodes: [] as Node[], edges: [] as Edge[] }

  const pr = productionRequirements.find(
    (p) => p.productCode === productCode && p.weekNumber === selectedWeek
  )
  const route = routes[0]
  const downtimeLookup = new Map<string, string>()
  for (const dt of downtimeData)
    if (dt.site_location) downtimeLookup.set(dt.resource, dt.site_location)

  // Determine the kitchen's site — from downtime data or connected lines
  let kitchenSite = downtimeLookup.get(route.kitchen) || ''
  if (!kitchenSite) {
    const connectedLines = [
      route.packingLine1,
      route.packingLine2,
      route.packingLine3,
      route.packingLine4,
    ]
    for (const ln of connectedLines) {
      if (!ln) continue
      const lineData = allLines.get(ln)
      if (lineData?.site) {
        kitchenSite = lineData.site
        break
      }
    }
  }

  // Build assigned lines lookup
  const assignedLines = new Map<string, { slot: number; rate: string }>()
  const lineSlots = [
    { line: route.packingLine1, rate: route.lineRunRate1, slot: 1 },
    { line: route.packingLine2, rate: route.lineRunRate2, slot: 2 },
    { line: route.packingLine3, rate: route.lineRunRate3, slot: 3 },
    { line: route.packingLine4, rate: route.lineRunRate4, slot: 4 },
  ]
  for (const s of lineSlots) {
    if (s.line) assignedLines.set(s.line, { slot: s.slot, rate: s.rate })
  }

  const nodes: Node[] = []
  const edges: Edge[] = []
  const PRODUCT_Y = 250
  const COL_GAP = 450

  // Offset shorter nodes so their handle centers align with the product node's handle center
  // Product node is ~180px tall (center at +90), kitchen ~55px (center at +27), base material ~95px (center at +47)
  const KITCHEN_Y = PRODUCT_Y + 63
  const BASEMATERIAL_Y = PRODUCT_Y + 43

  // Kitchen node (col 0)
  nodes.push({
    id: 'kitchen',
    type: 'flowKitchen',
    position: { x: 0, y: KITCHEN_Y },
    data: { name: route.kitchen },
  })

  // Base Material node (col 1)
  nodes.push({
    id: 'baseMaterial',
    type: 'flowBaseMaterial',
    position: { x: COL_GAP, y: BASEMATERIAL_Y },
    data: {
      code: route.baseProductCode,
      name: route.baseProductName,
      kitchenRate: route.kitchenRunRate || '0',
      onChangeKitchenRate: (val: string) =>
        onChangeRoute('kitchenRunRate', val),
    },
  })
  edges.push({
    id: 'k->bm',
    source: 'kitchen',
    target: 'baseMaterial',
    style: { stroke: '#1976d2', strokeWidth: 2 },
  })

  // Product node (col 2)
  nodes.push({
    id: 'product',
    type: 'flowProduct',
    position: { x: COL_GAP * 2, y: PRODUCT_Y },
    data: {
      code: route.productCode,
      name: route.productName,
      quantity: pr?.quantity || '',
      conversionFactor: pr?.conversionFactor || '',
      requiredTemperature: pr?.requiredTemperature ?? null,
      dontStartBefore: pr?.dontStartBefore ?? null,
      finishBefore: pr?.finishBefore ?? null,
      onChangeReq,
    },
  })
  edges.push({
    id: 'bm->p',
    source: 'baseMaterial',
    target: 'product',
    style: { stroke: '#7b1fa2', strokeWidth: 2 },
  })

  // Site lines (col 3) — only lines from the same site, assigned first
  const siteLineNames = Array.from(allLines.entries())
    .filter(([, ld]) => !kitchenSite || ld.site === kitchenSite)
    .map(([name]) => name)
    .sort((a, b) => {
      const aAssigned = assignedLines.has(a) ? 0 : 1
      const bAssigned = assignedLines.has(b) ? 0 : 1
      if (aAssigned !== bAssigned) return aAssigned - bAssigned
      return a.localeCompare(b)
    })
  const allLineNames = siteLineNames

  const ROW_H = 220
  const PRODUCT_HANDLE_Y = PRODUCT_Y + 90 // approximate handle center of product node
  const lineStartY =
    PRODUCT_HANDLE_Y - ((allLineNames.length - 1) * ROW_H) / 2 - 45 // offset for line node handle center (~45px)

  allLineNames.forEach((lineName, i) => {
    const assigned = assignedLines.get(lineName)
    const lineData = allLines.get(lineName)
    const id = `line-${lineName}`

    nodes.push({
      id,
      type: 'flowLine',
      position: { x: COL_GAP * 3, y: lineStartY + i * ROW_H },
      data: {
        name: lineName,
        runRate: assigned?.rate || '0',
        pr: assigned ? route.pr : '',
        site: lineData?.site || downtimeLookup.get(lineName) || '',
        assigned: !!assigned,
        onChangeRunRate: (val: string) => onChangeLineRate(lineName, val),
      },
    })

    // Edge: solid for assigned, dashed grey for unassigned
    edges.push({
      id: `p->${lineName}`,
      source: 'product',
      target: id,
      style: {
        stroke: assigned ? '#2e7d32' : '#e0e0e0',
        strokeWidth: assigned ? 2 : 1,
        strokeDasharray: assigned ? undefined : '5,5',
      },
    })
  })

  return { nodes, edges }
}

const buildBaseMaterialFlowGraph = (
  baseMaterialCode: string,
  packagingRoutes: SchedulerPackagingRoutesRowType[],
  productionRequirements: ProductionRequirementRowType[],
  downtimeData: WorkstationDownTimeRowType[],
  selectedWeek: number,
  onChangeRoute: (
    routeId: number,
    field: string,
    val: string,
    fullRow: Record<string, unknown>
  ) => void,
  onChangeReq: (
    reqId: number,
    field: string,
    val: string,
    fullRow: Record<string, unknown>
  ) => void
) => {
  const routes = packagingRoutes.filter(
    (r: SchedulerPackagingRoutesRowType) =>
      r.baseProductCode === baseMaterialCode
  )
  if (routes.length === 0) return { nodes: [] as Node[], edges: [] as Edge[] }

  const downtimeLookup = new Map<string, string>()
  for (const dt of downtimeData)
    if (dt.site_location) downtimeLookup.set(dt.resource, dt.site_location)

  const nodes: Node[] = []
  const edges: Edge[] = []
  const COL_GAP = 450
  const ROW_GAP = 280

  // Vertical center reference — will position kitchen & base material to align handles with products
  const CENTER_Y = 200

  // Kitchen (col 0) — offset down so handle aligns (~55px tall, center at +27, product center at +90)
  const kitchenName = routes[0].kitchen
  nodes.push({
    id: 'kitchen',
    type: 'flowKitchen',
    position: { x: 0, y: CENTER_Y + 63 },
    data: { name: kitchenName },
  })

  // Base Material (col 1) — offset down so handle aligns (~95px tall, center at +47)
  nodes.push({
    id: 'baseMaterial',
    type: 'flowBaseMaterial',
    position: { x: COL_GAP, y: CENTER_Y + 43 },
    data: {
      code: baseMaterialCode,
      name: routes[0].baseProductName,
      kitchenRate: routes[0].kitchenRunRate || '0',
      onChangeKitchenRate: (val: string) => {
        // Update kitchen rate on all routes for this base material
        for (const r of routes)
          onChangeRoute(
            r.id,
            'kitchenRunRate',
            val,
            r as unknown as Record<string, unknown>
          )
      },
    },
  })
  edges.push({
    id: 'k->bm',
    source: 'kitchen',
    target: 'baseMaterial',
    style: { stroke: '#1976d2', strokeWidth: 2 },
  })

  // Products (col 2) — unique products from these routes
  const uniqueProducts = new Map<string, SchedulerPackagingRoutesRowType>()
  for (const r of routes) {
    if (!uniqueProducts.has(r.productCode)) uniqueProducts.set(r.productCode, r)
  }
  const products = Array.from(uniqueProducts.values())
  const productStartY = CENTER_Y - ((products.length - 1) * ROW_GAP) / 2

  const allLineEntries = new Map<
    string,
    { runRate: string; pr: string; productId: string; site: string }
  >()

  products.forEach((route, pi) => {
    const pr = productionRequirements.find(
      (p) =>
        p.productCode === route.productCode && p.weekNumber === selectedWeek
    )
    const pId = `product-${route.productCode}`
    nodes.push({
      id: pId,
      type: 'flowProduct',
      position: { x: COL_GAP * 2, y: productStartY + pi * ROW_GAP },
      data: {
        code: route.productCode,
        name: route.productName,
        quantity: pr?.quantity || '',
        conversionFactor: pr?.conversionFactor || '',
        requiredTemperature: pr?.requiredTemperature ?? null,
        dontStartBefore: pr?.dontStartBefore ?? null,
        finishBefore: pr?.finishBefore ?? null,
        onChangeReq: pr
          ? (field: string, val: string) =>
              onChangeReq(
                pr.id,
                field,
                val,
                pr as unknown as Record<string, unknown>
              )
          : () => {},
      },
    })
    edges.push({
      id: `bm->${route.productCode}`,
      source: 'baseMaterial',
      target: pId,
      style: { stroke: '#7b1fa2', strokeWidth: 2 },
    })

    const lineEntries = [
      { line: route.packingLine1, rate: route.lineRunRate1, pr: route.pr },
      { line: route.packingLine2, rate: route.lineRunRate2, pr: route.pr },
      { line: route.packingLine3, rate: route.lineRunRate3, pr: route.pr },
      { line: route.packingLine4, rate: route.lineRunRate4, pr: route.pr },
    ]
    for (const entry of lineEntries) {
      if (!entry.line) continue
      if (!allLineEntries.has(entry.line)) {
        allLineEntries.set(entry.line, {
          runRate: entry.rate || '0',
          pr: entry.pr || '',
          productId: pId,
          site: downtimeLookup.get(entry.line) || '',
        })
      }
      edges.push({
        id: `${pId}->${entry.line}`,
        source: pId,
        target: `line-${entry.line}`,
        style: { stroke: '#2e7d32', strokeWidth: 2 },
        label: entry.rate ? `${entry.rate} u/hr` : undefined,
        labelStyle: { fontSize: 10, fill: '#666' },
      })
    }
  })

  // Line nodes (col 3)
  const lineArr = Array.from(allLineEntries.entries())
  const LINE_GAP = 220
  const lineStartY = CENTER_Y + 45 - ((lineArr.length - 1) * LINE_GAP) / 2
  lineArr.forEach(([lineName, info], i) => {
    nodes.push({
      id: `line-${lineName}`,
      type: 'flowLine',
      position: { x: COL_GAP * 3, y: lineStartY + i * LINE_GAP },
      data: {
        name: lineName,
        runRate: info.runRate,
        pr: info.pr,
        site: info.site,
        assigned: true,
      },
    })
  })

  return { nodes, edges }
}

// ─── Product Flow Diagram Component ─────────────────────────────────────────

const ProductFlowDiagram = ({
  focusedItem,
  packagingRoutes,
  productionRequirements,
  downtimeData,
  allLines,
  onSaveData,
  selectedWeek,
  onBack,
}: {
  focusedItem: NonNullable<FocusedItem>
  packagingRoutes: SchedulerPackagingRoutesRowType[]
  productionRequirements: ProductionRequirementRowType[]
  downtimeData: WorkstationDownTimeRowType[]
  allLines: Map<string, LineData>
  onSaveData: SaveDataFn
  selectedWeek: number
  onBack: () => void
}) => {
  // Track pending changes via refs to avoid re-rendering nodes (prevents input focus loss)
  const pendingRouteRef = useRef<Record<number, Record<string, unknown>>>({})
  const pendingReqRef = useRef<Record<number, Record<string, unknown>>>({})
  const pendingLineRatesRef = useRef<Record<string, string>>({})
  const [hasChanges, setHasChanges] = useState(false)

  // Reset pending changes when week or focused item changes
  useEffect(() => {
    pendingRouteRef.current = {}
    pendingReqRef.current = {}
    pendingLineRatesRef.current = {}
    setHasChanges(false)
  }, [selectedWeek, focusedItem.code, focusedItem.type])

  // Stable callbacks for product flow (single route)
  const productOnChangeRoute = useCallback(
    (field: string, val: string) => {
      const routes = packagingRoutes.filter(
        (r) => r.productCode === focusedItem.code
      )
      if (routes.length === 0) return
      const route = routes[0]
      if (!pendingRouteRef.current[route.id])
        pendingRouteRef.current[route.id] = {
          ...(route as unknown as Record<string, unknown>),
        }
      pendingRouteRef.current[route.id][field] = val
      setHasChanges(true)
    },
    [packagingRoutes, focusedItem.code]
  )

  const productOnChangeReq = useCallback(
    (field: string, val: string) => {
      const pr = productionRequirements.find(
        (p) =>
          p.productCode === focusedItem.code && p.weekNumber === selectedWeek
      )
      if (!pr) return
      if (!pendingReqRef.current[pr.id])
        pendingReqRef.current[pr.id] = {
          ...(pr as unknown as Record<string, unknown>),
        }
      pendingReqRef.current[pr.id][field] =
        field === 'requiredTemperature' ? (val ? Number(val) : '') : val
      setHasChanges(true)
    },
    [productionRequirements, focusedItem.code, selectedWeek]
  )

  const productOnChangeLineRate = useCallback(
    (lineName: string, val: string) => {
      pendingLineRatesRef.current[lineName] = val
      setHasChanges(true)
    },
    []
  )

  // Stable callbacks for base material flow (multiple routes)
  const bmOnChangeRoute = useCallback(
    (
      routeId: number,
      field: string,
      val: string,
      fullRow: Record<string, unknown>
    ) => {
      if (!pendingRouteRef.current[routeId])
        pendingRouteRef.current[routeId] = { ...fullRow }
      pendingRouteRef.current[routeId][field] = val
      setHasChanges(true)
    },
    []
  )

  const bmOnChangeReq = useCallback(
    (
      reqId: number,
      field: string,
      val: string,
      fullRow: Record<string, unknown>
    ) => {
      if (!pendingReqRef.current[reqId])
        pendingReqRef.current[reqId] = { ...fullRow }
      pendingReqRef.current[reqId][field] =
        field === 'requiredTemperature' ? (val ? Number(val) : '') : val
      setHasChanges(true)
    },
    []
  )

  // Save handler
  const handleSave = useCallback(() => {
    // For product flow: compute line slot assignments
    if (
      focusedItem.type === 'product' &&
      Object.keys(pendingLineRatesRef.current).length > 0
    ) {
      const routes = packagingRoutes.filter(
        (r) => r.productCode === focusedItem.code
      )
      if (routes.length > 0) {
        const route = routes[0]
        if (!pendingRouteRef.current[route.id])
          pendingRouteRef.current[route.id] = {
            ...(route as unknown as Record<string, unknown>),
          }

        const originalSlots = [
          { line: route.packingLine1, rate: route.lineRunRate1 },
          { line: route.packingLine2, rate: route.lineRunRate2 },
          { line: route.packingLine3, rate: route.lineRunRate3 },
          { line: route.packingLine4, rate: route.lineRunRate4 },
        ]

        // Build final assignments list
        const finalAssignments: { line: string; rate: string }[] = []

        // Keep original assignments unless removed
        for (const slot of originalSlots) {
          if (!slot.line) continue
          const newRate = pendingLineRatesRef.current[slot.line]
          if (newRate !== undefined) {
            if (newRate && newRate !== '0')
              finalAssignments.push({ line: slot.line, rate: newRate })
            // else: removed (rate set to 0/empty)
          } else {
            finalAssignments.push(slot)
          }
        }

        // Add new assignments
        for (const [lineName, rate] of Object.entries(
          pendingLineRatesRef.current
        )) {
          if (originalSlots.some((s) => s.line === lineName)) continue
          if (!rate || rate === '0') continue
          finalAssignments.push({ line: lineName, rate })
        }

        // Write to packingLine1-4 / lineRunRate1-4 (max 4)
        for (let i = 0; i < 4; i++) {
          pendingRouteRef.current[route.id][`packingLine${i + 1}`] =
            finalAssignments[i]?.line || ''
          pendingRouteRef.current[route.id][`lineRunRate${i + 1}`] =
            finalAssignments[i]?.rate || ''
        }
      }
    }

    // Save route changes
    if (Object.keys(pendingRouteRef.current).length > 0) {
      onSaveData('packing-routes', pendingRouteRef.current)
    }

    // Save requirement changes
    if (Object.keys(pendingReqRef.current).length > 0) {
      onSaveData('production-requirements', pendingReqRef.current, selectedWeek)
    }

    // Reset
    pendingRouteRef.current = {}
    pendingReqRef.current = {}
    pendingLineRatesRef.current = {}
    setHasChanges(false)
  }, [focusedItem, packagingRoutes, onSaveData, selectedWeek])

  const { nodes: flowNodes, edges: flowEdges } = useMemo(() => {
    if (focusedItem.type === 'product') {
      return buildProductFlowGraph(
        focusedItem.code,
        packagingRoutes,
        productionRequirements,
        downtimeData,
        allLines,
        selectedWeek,
        productOnChangeRoute,
        productOnChangeReq,
        productOnChangeLineRate
      )
    }
    return buildBaseMaterialFlowGraph(
      focusedItem.code,
      packagingRoutes,
      productionRequirements,
      downtimeData,
      selectedWeek,
      bmOnChangeRoute,
      bmOnChangeReq
    )
  }, [
    focusedItem,
    packagingRoutes,
    productionRequirements,
    downtimeData,
    allLines,
    selectedWeek,
    productOnChangeRoute,
    productOnChangeReq,
    productOnChangeLineRate,
    bmOnChangeRoute,
    bmOnChangeReq,
  ])

  const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges)

  useEffect(() => {
    setNodes(flowNodes)
    setEdges(flowEdges)
  }, [flowNodes, flowEdges, setNodes, setEdges])

  const title =
    focusedItem.type === 'product' ? 'Product Routing' : 'Base Material Routing'

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton
          onClick={onBack}
          size="small"
          sx={{
            bgcolor: 'white',
            border: '1px solid #e0e0e0',
            '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 0.5,
            border: '1px solid #e0e0e0',
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.7rem',
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            {title}
          </Typography>
          <Chip
            label={focusedItem.code}
            size="small"
            color="primary"
            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
          />
        </Paper>
        {hasChanges && (
          <IconButton
            onClick={handleSave}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              borderRadius: 1.5,
              px: 1.5,
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <SaveIcon sx={{ fontSize: 18, mr: 0.5 }} />
            <Typography variant="caption" fontWeight={700} color="white">
              Save
            </Typography>
          </IconButton>
        )}
      </Box>
      {focusedItem.type === 'product' && (
        <Box sx={{ position: 'absolute', bottom: 12, left: 12, zIndex: 10 }}>
          <Paper
            elevation={0}
            sx={{
              px: 1.5,
              py: 0.75,
              border: '1px solid #e0e0e0',
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{ width: 12, height: 3, bgcolor: '#2e7d32', borderRadius: 1 }}
            />
            <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
              Assigned
            </Typography>
            <Box
              sx={{
                width: 12,
                height: 0,
                border: '1.5px dashed #bdbdbd',
                borderRadius: 1,
                ml: 1,
              }}
            />
            <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
              Available
            </Typography>
          </Paper>
        </Box>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={focusNodeTypes}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.3}
        proOptions={{ hideAttribution: true }}
        nodesConnectable={false}
        nodesFocusable={false}
        edgesFocusable={false}
        elementsSelectable={false}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={16}
          size={1}
          color="#e0e0e0"
        />
        <Controls />
      </ReactFlow>
    </Box>
  )
}

// ─── Custom Nodes ────────────────────────────────────────────────────────────

const KitchenNode = memo(({ data }: NodeProps) => {
  const d = data as unknown as KitchenData & {
    onSelect: (type: string, name: string) => void
    selected?: boolean
    scheduleRows?: InputRow[]
  }
  const productCount = d.baseMaterials.reduce(
    (sum, bm) => sum + bm.products.length,
    0
  )
  return (
    <Paper
      elevation={d.selected ? 6 : 3}
      onClick={() => d.onSelect('kitchen', d.name)}
      sx={{
        p: 1.5,
        minWidth: 180,
        cursor: 'pointer',
        borderLeft: '4px solid #1976d2',
        borderRadius: 2,
        transition: 'box-shadow 0.2s, transform 0.15s',
        outline: d.selected ? '2px solid #1976d2' : 'none',
        bgcolor: d.selected ? '#e3f2fd' : undefined,
        '&:hover': {
          boxShadow: 6,
          transform: 'scale(1.03)',
        },
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#1976d2' }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <KitchenIcon sx={{ color: '#1976d2', fontSize: 18 }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {d.name}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {d.baseMaterials.length} base material
        {d.baseMaterials.length !== 1 ? 's' : ''} · {productCount} product
        {productCount !== 1 ? 's' : ''}
      </Typography>
      {d.scheduleRows && d.scheduleRows.length > 0 && (
        <Box sx={{ mt: 0.75 }}>
          <MiniGantt
            rows={d.scheduleRows}
            mode="kitchen"
            downtime={d.downtime}
          />
        </Box>
      )}
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: '#1976d2' }}
      />
    </Paper>
  )
})

const LineNode = memo(({ data }: NodeProps) => {
  const d = data as unknown as LineData & {
    onSelect: (type: string, name: string) => void
    selected?: boolean
    scheduleRows?: InputRow[]
  }
  return (
    <Paper
      elevation={d.selected ? 6 : 3}
      onClick={() => d.onSelect('line', d.name)}
      sx={{
        p: 1.5,
        minWidth: 170,
        cursor: 'pointer',
        borderLeft: '4px solid #2e7d32',
        borderRadius: 2,
        transition: 'box-shadow 0.2s, transform 0.15s',
        outline: d.selected ? '2px solid #2e7d32' : 'none',
        bgcolor: d.selected ? '#e8f5e9' : undefined,
        '&:hover': {
          boxShadow: 6,
          transform: 'scale(1.03)',
        },
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#2e7d32' }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <LineIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {d.name}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {d.products.length} product{d.products.length !== 1 ? 's' : ''}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
        {d.site && (
          <Chip
            label={d.site}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        )}
        {d.syncGroupCount > 0 && (
          <Chip
            icon={<SyncIcon sx={{ fontSize: '12px !important' }} />}
            label={`${d.syncGroupCount} sync`}
            size="small"
            color="info"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.6rem' }}
          />
        )}
      </Box>
      {d.scheduleRows && d.scheduleRows.length > 0 && (
        <Box sx={{ mt: 0.75 }}>
          <MiniGantt rows={d.scheduleRows} mode="line" downtime={d.downtime} />
        </Box>
      )}
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: '#2e7d32' }}
      />
    </Paper>
  )
})

const CipNode = memo(({ data }: NodeProps) => {
  const d = data as unknown as CipData & {
    onSelect: (type: string, name: string) => void
    selected?: boolean
  }
  return (
    <Paper
      elevation={d.selected ? 6 : 2}
      onClick={() => d.onSelect('cip', d.name)}
      sx={{
        p: 1.5,
        minWidth: 150,
        cursor: 'pointer',
        borderLeft: '4px solid #ed6c02',
        borderRadius: 2,
        transition: 'box-shadow 0.2s, transform 0.15s',
        outline: d.selected ? '2px solid #ed6c02' : 'none',
        bgcolor: d.selected ? '#fff3e0' : undefined,
        '&:hover': {
          boxShadow: 6,
          transform: 'scale(1.03)',
        },
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#ed6c02' }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <CleanIcon sx={{ color: '#ed6c02', fontSize: 18 }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {d.name}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block">
        Cleans {d.assets.length} asset{d.assets.length !== 1 ? 's' : ''}
      </Typography>
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: '#ed6c02' }}
      />
    </Paper>
  )
})

const SiteNode = memo(({ data }: NodeProps) => {
  const d = data as unknown as { label: string; width: number; height: number }
  return (
    <Box
      sx={{
        width: d.width,
        height: d.height,
        border: '2px dashed #90a4ae',
        borderRadius: 3,
        bgcolor: 'rgba(236, 239, 241, 0.35)',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -14,
          left: 16,
          bgcolor: '#fff',
          px: 1.5,
          py: 0.25,
          borderRadius: 1,
          border: '1px solid #90a4ae',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <LocationIcon sx={{ fontSize: 16, color: '#546e7a' }} />
        <Typography variant="subtitle2" fontWeight={700} color="#546e7a">
          {d.label}
        </Typography>
      </Box>
    </Box>
  )
})

const SyncGroupContainer = memo(({ data }: NodeProps) => {
  const d = data as unknown as {
    label: string
    type: 'temperature' | 'Active'
    width: number
    height: number
  }
  const isTemp = d.type === 'temperature'
  const color = isTemp ? '#1565c0' : '#ed6c02'
  return (
    <Box
      sx={{
        width: d.width,
        height: d.height,
        border: `2px dashed ${color}`,
        borderRadius: 3,
        bgcolor: isTemp
          ? 'rgba(21, 101, 192, 0.04)'
          : 'rgba(237, 108, 2, 0.04)',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -14,
          right: 16,
          bgcolor: '#fff',
          px: 1.5,
          py: 0.25,
          borderRadius: 1,
          border: `1px solid ${color}`,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {isTemp ? (
          <SyncIcon sx={{ fontSize: 14, color }} />
        ) : (
          <PeopleIcon sx={{ fontSize: 14, color }} />
        )}
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color }}>
          {d.label}
        </Typography>
      </Box>
    </Box>
  )
})

const nodeTypes: NodeTypes = {
  kitchen: KitchenNode,
  line: LineNode,
  cip: CipNode,
  site: SiteNode,
  syncGroup: SyncGroupContainer,
}

// ─── Side Panel ─────────────────────────────────────────────────────────────

type DetailSelection = {
  type: 'kitchen' | 'line' | 'cip'
  name: string
} | null

const SIDE_PANEL_WIDTH = 650

const AccordionSection = ({
  icon,
  title,
  count,
  defaultExpanded = true,
  children,
  renderContent,
}: {
  icon: React.ReactNode
  title: string
  count?: number
  defaultExpanded?: boolean
  children?: React.ReactNode
  renderContent?: () => React.ReactNode
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        mb: 1.5,
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
        sx={{
          bgcolor: 'grey.50',
          minHeight: 36,
          '& .MuiAccordionSummary-content': { my: 0.25 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {icon}
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
            {title}
          </Typography>
          {count !== undefined && (
            <Chip
              label={count}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
            />
          )}
        </Box>
      </AccordionSummary>
      {expanded && (
        <AccordionDetails sx={{ p: 1.5 }}>
          {renderContent ? renderContent() : children}
        </AccordionDetails>
      )}
    </Accordion>
  )
}

// Editable inline field — compact stacked label + input for side panel
const EditableField = ({
  label,
  value,
  unit,
  onChange,
}: {
  label: string
  value: string | number | null | undefined
  unit?: string
  onChange: (val: string) => void
}) => (
  <Box sx={{ flex: 1, minWidth: 70, maxWidth: 110 }}>
    <Typography
      sx={{
        fontSize: '0.6rem',
        color: 'text.secondary',
        mb: 0.25,
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
    <TextField
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      type="number"
      variant="outlined"
      InputProps={
        unit
          ? {
              endAdornment: (
                <InputAdornment position="end" sx={{ ml: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: 'text.disabled',
                      lineHeight: 1,
                    }}
                  >
                    {unit}
                  </Typography>
                </InputAdornment>
              ),
            }
          : undefined
      }
      sx={{
        width: '100%',
        bgcolor: 'white',
        '& .MuiInputBase-root': { height: 28 },
        '& .MuiInputBase-input': { py: 0, px: 0.75, fontSize: '0.75rem' },
      }}
    />
  </Box>
)

// Dropdown field — matches EditableField pattern
const DropdownField = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: unknown
  options: { value: string | number; label: string }[]
  onChange: (val: string) => void
}) => (
  <Box sx={{ flex: 1, minWidth: 70, maxWidth: 110 }}>
    <Typography
      sx={{
        fontSize: '0.6rem',
        color: 'text.secondary',
        mb: 0.25,
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
    <Select
      value={(value as string | number) ?? ''}
      onChange={(e) => onChange(String(e.target.value))}
      displayEmpty
      size="small"
      sx={{
        width: '100%',
        bgcolor: 'white',
        height: 28,
        fontSize: '0.75rem',
        '& .MuiSelect-select': { py: 0, px: 0.75 },
      }}
    >
      <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
        <em>None</em>
      </MenuItem>
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.75rem' }}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  </Box>
)

// Expandable product row — renders only name/code until clicked, then shows EditableFields
const KitchenProductRow = memo(
  ({
    product,
    route,
    getEditedValue,
    updateField,
    onFocusItem,
  }: {
    product: { code: string; name: string }
    route: SchedulerPackagingRoutesRowType | null
    getEditedValue: (
      api: string,
      rowId: number,
      field: string,
      original: string | number | null | undefined
    ) => unknown
    updateField: (
      api: string,
      rowId: number,
      field: string,
      value: string | number,
      fullRow: Record<string, unknown>
    ) => void
    onFocusItem: (item: FocusedItem) => void
  }) => {
    const [expanded, setExpanded] = useState(false)
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          borderRadius: 1,
          mb: 0.75,
          bgcolor: 'grey.50',
          '&:last-child': { mb: 0 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: route ? 'pointer' : 'default',
          }}
          onClick={() => route && setExpanded((v) => !v)}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              onClick={(e) => {
                e.stopPropagation()
                onFocusItem({ type: 'product', code: product.code })
              }}
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {product.code}
            </Typography>
            <Typography
              sx={{ fontSize: '0.65rem', color: 'text.secondary' }}
              noWrap
            >
              {product.name}
            </Typography>
          </Box>
          {route && (
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                color: 'text.disabled',
                transform: expanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          )}
        </Box>
        {expanded && route && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
            <EditableField
              label="Off-set"
              value={
                getEditedValue(
                  'packing-routes',
                  route.id,
                  'prepTime',
                  route.prepTime
                ) as string
              }
              unit="h"
              onChange={(v) =>
                updateField(
                  'packing-routes',
                  route.id,
                  'prepTime',
                  v,
                  route as unknown as Record<string, unknown>
                )
              }
            />
            <EditableField
              label="Prep Time"
              value={
                getEditedValue(
                  'packing-routes',
                  route.id,
                  'inputPrepTime',
                  route.inputPrepTime
                ) as string
              }
              unit="h"
              onChange={(v) =>
                updateField(
                  'packing-routes',
                  route.id,
                  'inputPrepTime',
                  v,
                  route as unknown as Record<string, unknown>
                )
              }
            />
            <EditableField
              label="Kitchen Rate"
              value={
                getEditedValue(
                  'packing-routes',
                  route.id,
                  'kitchenRunRate',
                  route.kitchenRunRate
                ) as string
              }
              unit="u/hr"
              onChange={(v) =>
                updateField(
                  'packing-routes',
                  route.id,
                  'kitchenRunRate',
                  v,
                  route as unknown as Record<string, unknown>
                )
              }
            />
            <EditableField
              label="Min Run"
              value={
                getEditedValue(
                  'packing-routes',
                  route.id,
                  'minRunTime',
                  route.minRunTime
                ) as string
              }
              unit="h"
              onChange={(v) =>
                updateField(
                  'packing-routes',
                  route.id,
                  'minRunTime',
                  v,
                  route as unknown as Record<string, unknown>
                )
              }
            />
            <EditableField
              label="Max Run"
              value={
                getEditedValue(
                  'packing-routes',
                  route.id,
                  'maxRunTime',
                  route.maxRunTime
                ) as string
              }
              unit="h"
              onChange={(v) =>
                updateField(
                  'packing-routes',
                  route.id,
                  'maxRunTime',
                  v,
                  route as unknown as Record<string, unknown>
                )
              }
            />
            <EditableField
              label="Max Batches"
              value={
                getEditedValue(
                  'packing-routes',
                  route.id,
                  'maxBatches',
                  route.maxBatches
                ) as string
              }
              onChange={(v) =>
                updateField(
                  'packing-routes',
                  route.id,
                  'maxBatches',
                  v,
                  route as unknown as Record<string, unknown>
                )
              }
            />
          </Box>
        )}
      </Paper>
    )
  }
)

// Expandable product row for line detail — shows name/chips, expands for EditableFields
const LineProductRow = memo(
  ({
    product,
    route,
    lineName,
    getEditedValue,
    updateField,
    onFocusItem,
  }: {
    product: { code: string; name: string; rate: number; pr: number }
    route: SchedulerPackagingRoutesRowType | null
    lineName: string
    getEditedValue: (
      api: string,
      rowId: number,
      field: string,
      original: string | number | null | undefined
    ) => unknown
    updateField: (
      api: string,
      rowId: number,
      field: string,
      value: string | number,
      fullRow: Record<string, unknown>
    ) => void
    onFocusItem: (item: FocusedItem) => void
  }) => {
    const [expanded, setExpanded] = useState(false)
    const lineField = route
      ? (
          [
            'packingLine1',
            'packingLine2',
            'packingLine3',
            'packingLine4',
          ] as const
        ).find((f) => (route as Record<string, unknown>)[f] === lineName)
      : null
    const rateField = lineField
      ? lineField.replace('packingLine', 'lineRunRate')
      : null
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1, borderRadius: 1, mb: 0.75, '&:last-child': { mb: 0 } }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: route ? 'pointer' : 'default',
          }}
          onClick={() => route && setExpanded((v) => !v)}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              onClick={(e) => {
                e.stopPropagation()
                onFocusItem({ type: 'product', code: product.code })
              }}
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {product.code}
            </Typography>
            <Typography
              sx={{ fontSize: '0.65rem', color: 'text.secondary' }}
              noWrap
            >
              {product.name}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexShrink: 0,
              ml: 1,
            }}
          >
            {route && (
              <>
                <Chip
                  label={route.kitchen}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.6rem' }}
                />
                <Chip
                  onClick={(e) => {
                    e.stopPropagation()
                    onFocusItem({
                      type: 'baseMaterial',
                      code: route.baseProductCode,
                    })
                  }}
                  label={route.baseProductCode}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    },
                  }}
                />
              </>
            )}
            {route && (
              <ExpandMoreIcon
                sx={{
                  fontSize: 16,
                  color: 'text.disabled',
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            )}
          </Box>
        </Box>
        {expanded && route && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {rateField && (
                <EditableField
                  label="Run Rate"
                  value={
                    getEditedValue(
                      'packing-routes',
                      route.id,
                      rateField,
                      (route as unknown as Record<string, string>)[rateField]
                    ) as string
                  }
                  unit="u/hr"
                  onChange={(v) =>
                    updateField(
                      'packing-routes',
                      route.id,
                      rateField,
                      v,
                      route as unknown as Record<string, unknown>
                    )
                  }
                />
              )}
              <EditableField
                label="PR"
                value={
                  getEditedValue(
                    'packing-routes',
                    route.id,
                    'pr',
                    route.pr
                  ) as string
                }
                unit="%"
                onChange={(v) =>
                  updateField(
                    'packing-routes',
                    route.id,
                    'pr',
                    v,
                    route as unknown as Record<string, unknown>
                  )
                }
              />
            </Box>
          </>
        )}
      </Paper>
    )
  }
)

// Expandable demand row — shows product name, expands for editable quantity/timing fields
const DemandRow = memo(
  ({
    pr,
    selectedReqIds,
    toggleReqSelection,
    getEditedValue,
    updateField,
  }: {
    pr: ProductionRequirementRowType
    selectedReqIds: Set<number>
    toggleReqSelection: (id: number) => void
    getEditedValue: (
      api: string,
      rowId: number,
      field: string,
      original: string | number | null | undefined
    ) => unknown
    updateField: (
      api: string,
      rowId: number,
      field: string,
      value: string | number,
      fullRow: Record<string, unknown>
    ) => void
  }) => {
    const [expanded, setExpanded] = useState(false)
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1, borderRadius: 1, mb: 0.75, '&:last-child': { mb: 0 } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
          }}
          onClick={() => setExpanded((v) => !v)}
        >
          <Checkbox
            size="small"
            checked={selectedReqIds.has(pr.id)}
            onChange={(e) => {
              e.stopPropagation()
              toggleReqSelection(pr.id)
            }}
            sx={{ p: 0.25 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
              {pr.productCode}
            </Typography>
            <Typography
              sx={{ fontSize: '0.65rem', color: 'text.secondary' }}
              noWrap
            >
              {pr.productName}
            </Typography>
          </Box>
          <ExpandMoreIcon
            sx={{
              fontSize: 16,
              color: 'text.disabled',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          />
        </Box>
        {expanded && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
            <EditableField
              label="Quantity"
              value={
                getEditedValue(
                  'production-requirements',
                  pr.id,
                  'quantity',
                  pr.quantity
                ) as string
              }
              onChange={(v) =>
                updateField(
                  'production-requirements',
                  pr.id,
                  'quantity',
                  v,
                  pr as unknown as Record<string, unknown>
                )
              }
            />
            <EditableField
              label="Start After"
              value={
                getEditedValue(
                  'production-requirements',
                  pr.id,
                  'dontStartBefore',
                  pr.dontStartBefore
                ) as string
              }
              unit="h"
              onChange={(v) =>
                updateField(
                  'production-requirements',
                  pr.id,
                  'dontStartBefore',
                  v,
                  pr as unknown as Record<string, unknown>
                )
              }
            />
            <EditableField
              label="Finish By"
              value={
                getEditedValue(
                  'production-requirements',
                  pr.id,
                  'finishBefore',
                  pr.finishBefore
                ) as string
              }
              unit="h"
              onChange={(v) =>
                updateField(
                  'production-requirements',
                  pr.id,
                  'finishBefore',
                  v,
                  pr as unknown as Record<string, unknown>
                )
              }
            />
          </Box>
        )}
      </Paper>
    )
  }
)

// Expandable product master row — shows product name, expands for editable conversion/temp fields
const MasterDataRow = memo(
  ({
    pr,
    getEditedValue,
    updateField,
  }: {
    pr: ProductionRequirementRowType
    getEditedValue: (
      api: string,
      rowId: number,
      field: string,
      original: string | number | null | undefined
    ) => unknown
    updateField: (
      api: string,
      rowId: number,
      field: string,
      value: string | number,
      fullRow: Record<string, unknown>
    ) => void
  }) => {
    const [expanded, setExpanded] = useState(false)
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1, borderRadius: 1, mb: 0.75, '&:last-child': { mb: 0 } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setExpanded((v) => !v)}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
              {pr.productCode}
            </Typography>
            <Typography
              sx={{ fontSize: '0.65rem', color: 'text.secondary' }}
              noWrap
            >
              {pr.productName}
            </Typography>
          </Box>
          <ExpandMoreIcon
            sx={{
              fontSize: 16,
              color: 'text.disabled',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          />
        </Box>
        {expanded && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              mt: 0.75,
            }}
          >
            <EditableField
              label="Conv. Factor"
              value={
                getEditedValue(
                  'production-requirements',
                  pr.id,
                  'conversionFactor',
                  pr.conversionFactor
                ) as string
              }
              onChange={(v) =>
                updateField(
                  'production-requirements',
                  pr.id,
                  'conversionFactor',
                  v,
                  pr as unknown as Record<string, unknown>
                )
              }
            />
            <DropdownField
              label="Req. Temp"
              value={getEditedValue(
                'production-requirements',
                pr.id,
                'requiredTemperature',
                pr.requiredTemperature
              )}
              options={[
                { value: 132, label: '132°C' },
                { value: 135, label: '135°C' },
              ]}
              onChange={(v) =>
                updateField(
                  'production-requirements',
                  pr.id,
                  'requiredTemperature',
                  v ? Number(v) : '',
                  pr as unknown as Record<string, unknown>
                )
              }
            />
          </Box>
        )}
      </Paper>
    )
  }
)

// Read-only info label
const InfoRow = ({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.25,
    }}
  >
    <Typography sx={{ fontSize: '0.7rem' }} color="text.secondary">
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
)

// Save callback type: (apiEndpoint, changedRows, week?) => void
type SaveDataFn = (
  api: string,
  data: Record<number, Record<string, unknown>>,
  week?: number
) => void

const SidePanel = memo(
  ({
    selection,
    isPending,
    kitchens,
    lines,
    cips,
    packagingRoutes,
    productionRequirements,
    synchronizedLines,
    cleaningMappingData,
    scheduleByKitchen,
    scheduleByLine,
    onSaveData,
    onUploadFile,
    onDeleteRows,
    selectedWeek,
    onWeekChange,
    onFocusItem,
  }: {
    selection: DetailSelection
    isPending?: boolean
    kitchens: Map<string, KitchenData>
    lines: Map<string, LineData>
    cips: Map<string, CipData>
    packagingRoutes: SchedulerPackagingRoutesRowType[]
    productionRequirements: ProductionRequirementRowType[]
    synchronizedLines: SynchronizedLinesRowType[]
    cleaningMappingData: SchedulerCleningMappingRowType[]
    scheduleByKitchen: Map<string, InputRow[]>
    scheduleByLine: Map<string, InputRow[]>
    onSaveData: SaveDataFn
    onUploadFile: (api: string, file: File, week?: number) => void
    onDeleteRows: (
      api: string,
      rows: Record<string, unknown>[],
      week?: number
    ) => Promise<void>
    selectedWeek: number
    onWeekChange: (week: number) => void
    onFocusItem: (item: FocusedItem) => void
  }) => {
    const [search, setSearch] = useState('')
    const [pendingChanges, setPendingChanges] = useState<
      Record<string, Record<number, Record<string, unknown>>>
    >({})
    const [selectedReqIds, setSelectedReqIds] = useState<Set<number>>(new Set())
    const reqFileInputRef = useRef<HTMLInputElement>(null)

    // Reset pending changes and selections when selection or week changes
    useEffect(() => {
      setPendingChanges({})
      setSelectedReqIds(new Set())
    }, [selection?.name, selection?.type])

    const hasPendingChanges = Object.values(pendingChanges).some(
      (apiChanges) => Object.keys(apiChanges).length > 0
    )

    const updateField = (
      api: string,
      rowId: number,
      field: string,
      value: string | number,
      fullRow: Record<string, unknown>
    ) => {
      setPendingChanges((prev) => ({
        ...prev,
        [api]: {
          ...(prev[api] || {}),
          [rowId]: { ...(prev[api]?.[rowId] || fullRow), [field]: value },
        },
      }))
    }

    const saveAllChanges = () => {
      for (const [api, rows] of Object.entries(pendingChanges)) {
        if (Object.keys(rows).length > 0) {
          const needsWeek = api === 'production-requirements'
          onSaveData(api, rows, needsWeek ? selectedWeek : undefined)
        }
      }
      setPendingChanges({})
    }

    // Upload handler for file input
    const handleUpload = (api: string, file: File) => {
      const needsWeek = api === 'production-requirements'
      onUploadFile(api, file, needsWeek ? selectedWeek : undefined)
    }

    // Delete selected rows
    const handleDeleteSelected = async (
      api: string,
      rows: ProductionRequirementRowType[]
    ) => {
      const needsWeek = api === 'production-requirements'
      await onDeleteRows(
        api,
        rows as unknown as Record<string, unknown>[],
        needsWeek ? selectedWeek : undefined
      )
      setSelectedReqIds(new Set())
    }

    const toggleReqSelection = (id: number) => {
      setSelectedReqIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
    }

    const getEditedValue = (
      api: string,
      rowId: number,
      field: string,
      original: string | number | null | undefined
    ) => {
      const val = pendingChanges[api]?.[rowId]?.[field] ?? original
      return val ?? ''
    }

    const matchesSearch = (text: unknown) => {
      if (!search) return true
      if (text == null) return false
      return String(text).toLowerCase().includes(search.toLowerCase())
    }

    const renderEmpty = () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          opacity: 0.5,
        }}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }}
        />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Select a kitchen, line, or CIP
        </Typography>
        <Typography variant="caption" color="text.disabled" textAlign="center">
          Click a node to view details
        </Typography>
      </Box>
    )

    const renderKitchenDetail = (kitchen: KitchenData) => {
      // Only compute counts upfront (cheap) — heavy filtering/rendering deferred to renderContent
      const kitchenProductCodes = kitchen.baseMaterials.flatMap((bm) =>
        bm.products.map((p) => p.code)
      )
      const demandCount = productionRequirements.filter((pr) =>
        kitchenProductCodes.includes(pr.productCode)
      ).length

      return (
        <Stack spacing={0}>
          <AccordionSection
            icon={<ProductIcon sx={{ fontSize: 18, color: '#1976d2' }} />}
            title="Base Materials & Products"
            count={kitchen.baseMaterials.length}
            defaultExpanded={false}
            renderContent={() => {
              const kitchenRouteMap = new Map<
                string,
                SchedulerPackagingRoutesRowType
              >()
              for (const r of packagingRoutes) {
                if (
                  r.kitchen === kitchen.name &&
                  !kitchenRouteMap.has(r.productCode)
                ) {
                  kitchenRouteMap.set(r.productCode, r)
                }
              }
              const filteredBMs = kitchen.baseMaterials.filter(
                (bm) =>
                  matchesSearch(bm.code) ||
                  matchesSearch(bm.name) ||
                  bm.products.some(
                    (p) => matchesSearch(p.code) || matchesSearch(p.name)
                  )
              )
              return (
                <>
                  {filteredBMs.map((bm) => (
                    <Paper
                      key={bm.code}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        mb: 1,
                        '&:last-child': { mb: 0 },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 0.75,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: 700,
                              lineHeight: 1.3,
                            }}
                            noWrap
                          >
                            {bm.name || bm.code}
                          </Typography>
                          <Typography
                            onClick={() =>
                              onFocusItem({
                                type: 'baseMaterial',
                                code: bm.code,
                              })
                            }
                            sx={{
                              fontSize: '0.65rem',
                              color: 'primary.main',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {bm.code}
                          </Typography>
                        </Box>
                        <Chip
                          icon={
                            <SpeedIcon sx={{ fontSize: '12px !important' }} />
                          }
                          label={`${bm.rate} u/hr`}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            ml: 1,
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                      <Divider sx={{ my: 0.75 }} />
                      {bm.products
                        .filter(
                          (p) =>
                            !search ||
                            matchesSearch(p.code) ||
                            matchesSearch(p.name) ||
                            matchesSearch(bm.code) ||
                            matchesSearch(bm.name)
                        )
                        .map((p) => (
                          <KitchenProductRow
                            key={p.code}
                            product={p}
                            route={kitchenRouteMap.get(p.code) || null}
                            getEditedValue={getEditedValue}
                            updateField={updateField}
                            onFocusItem={onFocusItem}
                          />
                        ))}
                    </Paper>
                  ))}
                </>
              )
            }}
          />

          <AccordionSection
            icon={<AssignmentIcon sx={{ fontSize: 18, color: '#7b1fa2' }} />}
            title="Production Requirements"
            count={demandCount}
            defaultExpanded={false}
            renderContent={() => {
              const kitchenDemand = productionRequirements.filter((pr) =>
                kitchenProductCodes.includes(pr.productCode)
              )
              const filteredDemand = kitchenDemand.filter(
                (pr) =>
                  matchesSearch(pr.productCode) || matchesSearch(pr.productName)
              )
              return (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Checkbox
                        size="small"
                        checked={
                          filteredDemand.length > 0 &&
                          filteredDemand.every((pr) =>
                            selectedReqIds.has(pr.id)
                          )
                        }
                        indeterminate={
                          filteredDemand.some((pr) =>
                            selectedReqIds.has(pr.id)
                          ) &&
                          !filteredDemand.every((pr) =>
                            selectedReqIds.has(pr.id)
                          )
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedReqIds(
                              new Set([
                                ...selectedReqIds,
                                ...filteredDemand.map((pr) => pr.id),
                              ])
                            )
                          } else {
                            const next = new Set(selectedReqIds)
                            filteredDemand.forEach((pr) => next.delete(pr.id))
                            setSelectedReqIds(next)
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Select all
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Upload CSV">
                        <IconButton
                          size="small"
                          onClick={() => reqFileInputRef.current?.click()}
                        >
                          <UploadFileIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      {selectedReqIds.size > 0 && (
                        <Tooltip
                          title={`Delete ${selectedReqIds.size} selected`}
                        >
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleDeleteSelected(
                                'production-requirements',
                                filteredDemand.filter((pr) =>
                                  selectedReqIds.has(pr.id)
                                )
                              )
                            }
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  {filteredDemand.map((pr) => (
                    <DemandRow
                      key={`${pr.productCode}-${pr.weekNumber}`}
                      pr={pr}
                      selectedReqIds={selectedReqIds}
                      toggleReqSelection={toggleReqSelection}
                      getEditedValue={getEditedValue}
                      updateField={updateField}
                    />
                  ))}
                </>
              )
            }}
          />

          <AccordionSection
            icon={<ScienceIcon sx={{ fontSize: 18, color: '#00695c' }} />}
            title="Product Master Data"
            count={demandCount}
            defaultExpanded={false}
            renderContent={() => {
              const kitchenDemand = productionRequirements.filter((pr) =>
                kitchenProductCodes.includes(pr.productCode)
              )
              const filteredDemand = kitchenDemand.filter(
                (pr) =>
                  matchesSearch(pr.productCode) || matchesSearch(pr.productName)
              )
              return (
                <>
                  {filteredDemand.map((pr) => (
                    <MasterDataRow
                      key={`master-${pr.productCode}-${pr.weekNumber}`}
                      pr={pr}
                      getEditedValue={getEditedValue}
                      updateField={updateField}
                    />
                  ))}
                </>
              )
            }}
          />

          {(scheduleByKitchen.get(kitchen.name)?.length || 0) > 0 && (
            <AccordionSection
              icon={<ScheduleIcon sx={{ fontSize: 18, color: '#2e7d32' }} />}
              title="Schedule"
              count={scheduleByKitchen.get(kitchen.name)?.length || 0}
              defaultExpanded={false}
              renderContent={() => {
                const rows = scheduleByKitchen.get(kitchen.name) || []
                const totalProdHours = rows.reduce(
                  (sum, r) =>
                    sum +
                    ((r.kitchen_end_time || 0) - (r.kitchen_start_time || 0)),
                  0
                )
                const totalQty = rows.reduce(
                  (sum, r) => sum + (r.quantity || 0),
                  0
                )
                const util = Math.round((totalProdHours / 168) * 100)
                return (
                  <>
                    <Box
                      sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}
                    >
                      <Chip
                        label={`${util}% utilised`}
                        size="small"
                        color={
                          util > 80
                            ? 'success'
                            : util > 50
                              ? 'warning'
                              : 'default'
                        }
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                      <Chip
                        label={`${totalProdHours.toFixed(0)}h production`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                      <Chip
                        label={`${totalQty.toFixed(0)} units`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    </Box>
                    {rows.map((r, i) => (
                      <Paper
                        key={i}
                        variant="outlined"
                        sx={{
                          p: 0.75,
                          borderRadius: 1,
                          mb: 0.5,
                          '&:last-child': { mb: 0 },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                          >
                            {r.productCode}
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.6rem', color: 'text.secondary' }}
                          >
                            {r.kitchen_start_time}h – {r.kitchen_end_time}h
                          </Typography>
                        </Box>
                      </Paper>
                    ))}
                  </>
                )
              }}
            />
          )}
        </Stack>
      )
    }

    const renderLineDetail = (line: LineData) => {
      // Only compute cheap counts upfront — heavy work deferred to renderContent
      const syncGroups = synchronizedLines.filter((sg) =>
        sg.lines
          ?.split(',')
          .map((l: string) => l.trim())
          .includes(line.name)
      )
      const lineProductCodes = line.products.map((p) => p.code)
      const demandCount = productionRequirements.filter((pr) =>
        lineProductCodes.includes(pr.productCode)
      ).length
      const cleaningCount = cleaningMappingData.filter(
        (cm) => cm.asset === line.name
      ).length

      return (
        <Stack spacing={0}>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
            {line.site && (
              <Chip
                icon={<LocationIcon sx={{ fontSize: '12px !important' }} />}
                label={line.site}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            )}
            {syncGroups.length > 0 && (
              <Chip
                icon={<SyncIcon sx={{ fontSize: '12px !important' }} />}
                label={`Sync: ${syncGroups.map((g) => g.groupName).join(', ')}`}
                size="small"
                color="info"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            )}
          </Box>

          <AccordionSection
            icon={<ProductIcon sx={{ fontSize: 18, color: '#2e7d32' }} />}
            title="Products"
            count={line.products.length}
            defaultExpanded={false}
            renderContent={() => {
              const lineRouteMap = new Map<
                string,
                SchedulerPackagingRoutesRowType
              >()
              for (const r of packagingRoutes) {
                if (
                  (r.packingLine1 === line.name ||
                    r.packingLine2 === line.name ||
                    r.packingLine3 === line.name ||
                    r.packingLine4 === line.name) &&
                  !lineRouteMap.has(r.productCode)
                ) {
                  lineRouteMap.set(r.productCode, r)
                }
              }
              const filteredProducts = line.products.filter(
                (p) => matchesSearch(p.code) || matchesSearch(p.name)
              )
              return (
                <>
                  {filteredProducts.map((p) => (
                    <LineProductRow
                      key={p.code}
                      product={p}
                      route={lineRouteMap.get(p.code) || null}
                      lineName={line.name}
                      getEditedValue={getEditedValue}
                      updateField={updateField}
                      onFocusItem={onFocusItem}
                    />
                  ))}
                </>
              )
            }}
          />

          <AccordionSection
            icon={<AssignmentIcon sx={{ fontSize: 18, color: '#7b1fa2' }} />}
            title="Production Requirements"
            count={demandCount}
            defaultExpanded={false}
            renderContent={() => {
              const lineDemand = productionRequirements.filter((pr) =>
                lineProductCodes.includes(pr.productCode)
              )
              const filteredDemand = lineDemand.filter(
                (pr) =>
                  matchesSearch(pr.productCode) || matchesSearch(pr.productName)
              )
              return (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Checkbox
                        size="small"
                        checked={
                          filteredDemand.length > 0 &&
                          filteredDemand.every((pr) =>
                            selectedReqIds.has(pr.id)
                          )
                        }
                        indeterminate={
                          filteredDemand.some((pr) =>
                            selectedReqIds.has(pr.id)
                          ) &&
                          !filteredDemand.every((pr) =>
                            selectedReqIds.has(pr.id)
                          )
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedReqIds(
                              new Set([
                                ...selectedReqIds,
                                ...filteredDemand.map((pr) => pr.id),
                              ])
                            )
                          } else {
                            const next = new Set(selectedReqIds)
                            filteredDemand.forEach((pr) => next.delete(pr.id))
                            setSelectedReqIds(next)
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Select all
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Upload CSV">
                        <IconButton
                          size="small"
                          onClick={() => reqFileInputRef.current?.click()}
                        >
                          <UploadFileIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      {selectedReqIds.size > 0 && (
                        <Tooltip
                          title={`Delete ${selectedReqIds.size} selected`}
                        >
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleDeleteSelected(
                                'production-requirements',
                                filteredDemand.filter((pr) =>
                                  selectedReqIds.has(pr.id)
                                )
                              )
                            }
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  {filteredDemand.map((pr) => (
                    <DemandRow
                      key={`${pr.productCode}-${pr.weekNumber}`}
                      pr={pr}
                      selectedReqIds={selectedReqIds}
                      toggleReqSelection={toggleReqSelection}
                      getEditedValue={getEditedValue}
                      updateField={updateField}
                    />
                  ))}
                </>
              )
            }}
          />

          <AccordionSection
            icon={<ScienceIcon sx={{ fontSize: 18, color: '#00695c' }} />}
            title="Product Master Data"
            count={demandCount}
            defaultExpanded={false}
            renderContent={() => {
              const lineDemand = productionRequirements.filter((pr) =>
                lineProductCodes.includes(pr.productCode)
              )
              const filteredDemand = lineDemand.filter(
                (pr) =>
                  matchesSearch(pr.productCode) || matchesSearch(pr.productName)
              )
              return (
                <>
                  {filteredDemand.map((pr) => (
                    <MasterDataRow
                      key={`master-${pr.productCode}-${pr.weekNumber}`}
                      pr={pr}
                      getEditedValue={getEditedValue}
                      updateField={updateField}
                    />
                  ))}
                </>
              )
            }}
          />

          <AccordionSection
            icon={<CleanIcon sx={{ fontSize: 18, color: '#ed6c02' }} />}
            title="Cleaning Programs"
            count={cleaningCount}
            defaultExpanded={false}
            renderContent={() => {
              const lineCleaning = cleaningMappingData.filter(
                (cm) => cm.asset === line.name
              )
              return (
                <>
                  {lineCleaning.map((cm, i) => (
                    <Paper
                      key={i}
                      variant="outlined"
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        mb: 0.75,
                        '&:last-child': { mb: 0 },
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                        {cm.programName}
                      </Typography>
                      <Typography
                        sx={{ fontSize: '0.65rem', color: 'text.secondary' }}
                      >
                        CIP: {cm.cip}
                      </Typography>
                    </Paper>
                  ))}
                </>
              )
            }}
          />

          <AccordionSection
            icon={<SyncIcon sx={{ fontSize: 18, color: '#0288d1' }} />}
            title="Synchronized Groups"
            count={syncGroups.length}
            defaultExpanded={false}
            renderContent={() => (
              <>
                {syncGroups.map((sg) => (
                  <Paper
                    key={sg.id}
                    variant="outlined"
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      mb: 0.75,
                      '&:last-child': { mb: 0 },
                    }}
                  >
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      {sg.groupName}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        flexWrap: 'wrap',
                        mt: 0.5,
                      }}
                    >
                      {sg.lines?.split(',').map((l: string) => (
                        <Chip
                          key={l.trim()}
                          label={l.trim()}
                          size="small"
                          variant={
                            l.trim() === line.name ? 'filled' : 'outlined'
                          }
                          color={l.trim() === line.name ? 'primary' : 'default'}
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      ))}
                    </Box>
                    <InfoRow
                      label="Type"
                      value={sg.productMasterType || 'temperature'}
                    />
                    <InfoRow
                      label="Changeover Time"
                      value={`${sg.changeoverTime}h`}
                    />
                    {sg.productMasterType === 'Active' && (
                      <>
                        <InfoRow label="Max Active Lines" value={sg.active} />
                        {sg.periods && (
                          <InfoRow label="Periods" value={sg.periods} />
                        )}
                      </>
                    )}
                  </Paper>
                ))}
              </>
            )}
          />

          {(scheduleByLine.get(line.name)?.length || 0) > 0 && (
            <AccordionSection
              icon={<ScheduleIcon sx={{ fontSize: 18, color: '#2e7d32' }} />}
              title="Schedule"
              count={scheduleByLine.get(line.name)?.length || 0}
              defaultExpanded={false}
              renderContent={() => {
                const rows = scheduleByLine.get(line.name) || []
                const totalProdHours = rows.reduce(
                  (sum, r) =>
                    sum + ((r.line_end_time || 0) - (r.line_start_time || 0)),
                  0
                )
                const totalQty = rows.reduce(
                  (sum, r) => sum + (r.quantity || 0),
                  0
                )
                const util = Math.round((totalProdHours / 168) * 100)
                return (
                  <>
                    <Box
                      sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}
                    >
                      <Chip
                        label={`${util}% utilised`}
                        size="small"
                        color={
                          util > 80
                            ? 'success'
                            : util > 50
                              ? 'warning'
                              : 'default'
                        }
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                      <Chip
                        label={`${totalProdHours.toFixed(0)}h production`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                      <Chip
                        label={`${totalQty.toFixed(0)} units`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    </Box>
                    {rows.map((r, i) => (
                      <Paper
                        key={i}
                        variant="outlined"
                        sx={{
                          p: 0.75,
                          borderRadius: 1,
                          mb: 0.5,
                          '&:last-child': { mb: 0 },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                          >
                            {r.productCode}
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.6rem', color: 'text.secondary' }}
                          >
                            {r.line_start_time}h – {r.line_end_time}h
                          </Typography>
                        </Box>
                      </Paper>
                    ))}
                  </>
                )
              }}
            />
          )}
        </Stack>
      )
    }

    const renderCipDetail = (cip: CipData) => {
      const cipPrograms = cleaningMappingData.filter(
        (cm) => cm.cip === cip.name
      )

      return (
        <Stack spacing={0}>
          <AccordionSection
            icon={<CleanIcon sx={{ fontSize: 18, color: '#ed6c02' }} />}
            title="Connected Assets"
            count={cip.assets.length}
          >
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {cip.assets.map((a) => {
                const isKitchen = kitchens.has(a)
                const isLine = lines.has(a)
                return (
                  <Chip
                    key={a}
                    icon={
                      isKitchen ? (
                        <KitchenIcon sx={{ fontSize: '14px !important' }} />
                      ) : isLine ? (
                        <LineIcon sx={{ fontSize: '14px !important' }} />
                      ) : undefined
                    }
                    label={a}
                    size="small"
                    variant="outlined"
                    color={
                      isKitchen ? 'primary' : isLine ? 'success' : 'default'
                    }
                    sx={{ height: 22, fontSize: '0.7rem' }}
                  />
                )
              })}
            </Box>
          </AccordionSection>

          {cipPrograms.length > 0 && (
            <AccordionSection
              icon={<ScheduleIcon sx={{ fontSize: 18, color: '#546e7a' }} />}
              title="Cleaning Programs"
              count={cipPrograms.length}
            >
              {cipPrograms.map((cm, i) => (
                <Paper
                  key={i}
                  variant="outlined"
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    mb: 0.75,
                    '&:last-child': { mb: 0 },
                  }}
                >
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    {cm.programName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      color: 'text.secondary',
                      mb: 0.5,
                    }}
                  >
                    Asset: {cm.asset}
                  </Typography>
                  <Divider sx={{ my: 0.5 }} />
                  <InfoRow
                    label="Total Time"
                    value={`${cm.totalTargetTime} min`}
                  />
                  <InfoRow label="Pre-Rinse" value={`${cm.preRinseTime} min`} />
                  <InfoRow label="Caustic" value={`${cm.causticTime} min`} />
                  <InfoRow label="Drain" value={`${cm.drainTime} min`} />
                  <InfoRow
                    label="Final Rinse"
                    value={`${cm.finalRinseTime} min`}
                  />
                  <InfoRow label="Prep Time" value={`${cm.prepTime} min`} />
                  {cm.cipChannel && (
                    <InfoRow label="Channel" value={cm.cipChannel} />
                  )}
                </Paper>
              ))}
            </AccordionSection>
          )}
        </Stack>
      )
    }

    const hasSelection =
      selection &&
      ((selection.type === 'kitchen' && kitchens.has(selection.name)) ||
        (selection.type === 'line' && lines.has(selection.name)) ||
        (selection.type === 'cip' && cips.has(selection.name)))

    const icon = !selection ? null : selection.type === 'kitchen' ? (
      <KitchenIcon sx={{ color: '#1976d2' }} />
    ) : selection.type === 'line' ? (
      <LineIcon sx={{ color: '#2e7d32' }} />
    ) : (
      <CleanIcon sx={{ color: '#ed6c02' }} />
    )

    const borderColor = !selection
      ? '#e0e0e0'
      : selection.type === 'kitchen'
        ? '#1976d2'
        : selection.type === 'line'
          ? '#2e7d32'
          : '#ed6c02'

    const title = !selection
      ? ''
      : selection.type === 'kitchen'
        ? `Kitchen`
        : selection.type === 'line'
          ? `Line`
          : `CIP`

    return (
      <Paper
        elevation={0}
        sx={{
          width: SIDE_PANEL_WIDTH,
          minWidth: SIDE_PANEL_WIDTH,
          height: '100%',
          borderLeft: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `2px solid ${borderColor}`,
            bgcolor: hasSelection ? 'grey.50' : 'transparent',
            transition: 'all 0.2s ease',
          }}
        >
          {hasSelection ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {icon}
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    color: 'text.secondary',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {title}
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: '0.9rem', fontWeight: 700, mt: 0.25 }}
              >
                {selection!.name}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              Details
            </Typography>
          )}
        </Box>

        {/* Week selector & Search & Save bar */}
        {hasSelection && (
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Week
              </Typography>
              <Select
                value={selectedWeek}
                onChange={(e) => onWeekChange(Number(e.target.value))}
                size="small"
                sx={{
                  minWidth: 70,
                  fontSize: '0.8rem',
                  bgcolor: 'white',
                  '& .MuiSelect-select': { py: 0.5 },
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((w) => (
                  <MenuItem key={w} value={w}>
                    Week {w}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                placeholder="Search products, materials..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ fontSize: 18, color: 'text.disabled' }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ visibility: search ? 'visible' : 'hidden' }}
                    >
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  bgcolor: 'white',
                  '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' },
                }}
              />
              {hasPendingChanges && (
                <IconButton
                  onClick={saveAllChanges}
                  color="primary"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderRadius: 1.5,
                    px: 1.5,
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <SaveIcon sx={{ fontSize: 18, mr: 0.5 }} />
                  <Typography variant="caption" fontWeight={700} color="white">
                    Save
                  </Typography>
                </IconButton>
              )}
            </Box>
          </Box>
        )}

        {/* Hidden file input for CSV upload */}
        <input
          ref={reqFileInputRef}
          type="file"
          accept=".csv,.xlsx"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) {
              handleUpload('production-requirements', file)
              e.target.value = ''
            }
          }}
        />

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, position: 'relative' }}>
          {isPending && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.7)',
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
          {!hasSelection
            ? renderEmpty()
            : selection!.type === 'kitchen'
              ? renderKitchenDetail(kitchens.get(selection!.name)!)
              : selection!.type === 'line'
                ? renderLineDetail(lines.get(selection!.name)!)
                : renderCipDetail(cips.get(selection!.name)!)}
        </Box>
      </Paper>
    )
  }
)

// ─── Main Component ──────────────────────────────────────────────────────────

export type FactoryLayoutFilter = {
  type: 'kitchen' | 'line' | 'cip'
  name: string
} | null

type FactoryLayoutProps = {
  schedulerId: string
  iteration: string
  packagingRoutes: SchedulerPackagingRoutesRowType[]
  downtimeData: WorkstationDownTimeRowType[]
  cleaningMapping: SchedulerCleningMappingRowType[]
  productionRequirements: ProductionRequirementRowType[]
  synchronizedLines: SynchronizedLinesRowType[]
  outputData?: InputRow[]
  // Sorted list of weeks consolidated into a single solve. When length >= 2,
  // the scheduler_api overlap-buckets each spanning run into every week it
  // touches with ABSOLUTE consolidated-horizon times — so the schedule
  // accordion clips times into the current week's [0, 168] slice and drops
  // rows whose per-resource activity has no overlap with this week. Absent
  // or length < 2 ⇒ single-week mode, no clipping.
  consolidatedWeeks?: number[]
  dataSummary?: { name: string; count: number }[]
  onFilterChange?: (filter: FactoryLayoutFilter) => void
  onSaveData: (
    api: string,
    data: Record<number, Record<string, unknown>>,
    week?: number
  ) => void
  onUploadFile: (api: string, file: File, week?: number) => void
  onDeleteRows: (
    api: string,
    rows: Record<string, unknown>[],
    week?: number
  ) => Promise<void>
  selectedWeek: number
  onWeekChange: (week: number) => void
  pendingComment?: {
    productCode: string
    resourceName: string
    resourceType: 'kitchen' | 'line'
    startTime: number
    endTime: number
  } | null
  onClearPendingComment?: () => void
  notProducedIds?: Set<string>
  onResetNotProduced?: () => void
  /** When true the ScheduleControlPanel side panel is suppressed so the
   *  ReactFlow diagram takes full width. The caller is responsible for
   *  rendering the panel elsewhere (e.g. a bottom dock). */
  hideSidePanel?: boolean
}

export const FactoryLayout = memo(
  ({
    schedulerId,
    iteration,
    packagingRoutes,
    downtimeData,
    cleaningMapping,
    productionRequirements,
    synchronizedLines,
    outputData = [],
    consolidatedWeeks,
    dataSummary,
    onFilterChange,
    onSaveData,
    onUploadFile,
    onDeleteRows,
    selectedWeek,
    onWeekChange,
    pendingComment,
    onClearPendingComment,
    notProducedIds,
    onResetNotProduced,
    hideSidePanel = false,
  }: FactoryLayoutProps) => {
    const [selection, setSelection] = useState<DetailSelection>(null)
    const [focusedItem, setFocusedItem] = useState<FocusedItem>(null)
    const selectionRef = useRef<string | null>(null)
    const [isPanelPending, startPanelTransition] = useTransition()
    const renderCountRef = useRef(0)
    renderCountRef.current++

    // Stable callback ref — avoids putting onSelect into useMemo deps
    const onFilterChangeRef = useRef(onFilterChange)
    onFilterChangeRef.current = onFilterChange

    const onDeselect = useCallback(() => {
      const prevId = selectionRef.current
      if (!prevId) return
      selectionRef.current = null

      setNodes((nds) =>
        nds.map((n) =>
          n.id === prevId && n.data.selected
            ? { ...n, data: { ...n.data, selected: false } }
            : n
        )
      )

      startPanelTransition(() => {
        setSelection(null)
        onFilterChangeRef.current?.(null)
      })
    }, [setNodes, startPanelTransition])

    const onSelect = useCallback(
      (type: string, name: string) => {
        const nodeId = `${type}-${name}`
        const prevId = selectionRef.current
        selectionRef.current = nodeId

        // HIGH PRIORITY: update node highlight immediately on the diagram
        setNodes((nds) =>
          nds.map((n) => {
            if (n.id === nodeId && !n.data.selected)
              return { ...n, data: { ...n.data, selected: true } }
            if (n.id === prevId && n.data.selected)
              return { ...n, data: { ...n.data, selected: false } }
            return n
          })
        )

        // LOW PRIORITY: defer side panel + parent updates so the diagram stays responsive
        startPanelTransition(() => {
          setSelection({ type: type as 'kitchen' | 'line' | 'cip', name })

          onFilterChangeRef.current?.({
            type: type as 'kitchen' | 'line' | 'cip',
            name,
          })
        })
      },
      [setNodes, startPanelTransition]
    )

    // Build data maps from packaging routes
    const { kitchens, lines, cips, scheduleByKitchen, scheduleByLine } =
      useMemo(() => {
        const kitchenMap = new Map<string, KitchenData>()
        const lineMap = new Map<string, LineData>()
        const cipMap = new Map<string, CipData>()

        // Build downtime lookup
        const downtimeLookup = new Map<
          string,
          { downTime: string | null; site: string }
        >()
        for (const dt of downtimeData) {
          downtimeLookup.set(dt.resource, {
            downTime: dt.downTime || null,
            site: dt.site_location,
          })
        }

        // Process packaging routes
        for (const route of packagingRoutes) {
          const kitchenName = route.kitchen
          if (!kitchenName) continue

          // Build kitchen data
          if (!kitchenMap.has(kitchenName)) {
            const dt = downtimeLookup.get(kitchenName)
            kitchenMap.set(kitchenName, {
              name: kitchenName,
              baseMaterials: [],
              downtime: dt?.downTime || null,
            })
          }
          const kitchen = kitchenMap.get(kitchenName)!

          // Find or create base material entry
          let bm = kitchen.baseMaterials.find(
            (b) => b.code === route.baseProductCode
          )
          if (!bm) {
            bm = {
              code: route.baseProductCode,
              name: route.baseProductName,
              rate: parseFloat(route.kitchenRunRate) || 0,
              products: [],
            }
            kitchen.baseMaterials.push(bm)
          }

          // Add product if not already there
          if (!bm.products.find((p) => p.code === route.productCode)) {
            bm.products.push({
              code: route.productCode,
              name: route.productName,
            })
          }

          // Process packing lines (up to 4)
          const lineEntries = [
            {
              line: route.packingLine1,
              rate: route.lineRunRate1,
              pr: route.pr,
            },
            { line: route.packingLine2, rate: route.lineRunRate2 },
            { line: route.packingLine3, rate: route.lineRunRate3 },
            { line: route.packingLine4, rate: route.lineRunRate4 },
          ]

          for (const entry of lineEntries) {
            if (!entry.line) continue
            if (!lineMap.has(entry.line)) {
              const dt = downtimeLookup.get(entry.line)
              lineMap.set(entry.line, {
                name: entry.line,
                products: [],
                downtime: dt?.downTime || null,
                site: dt?.site || '',
                syncGroupCount: 0,
              })
            }
            const line = lineMap.get(entry.line)!
            if (!line.products.find((p) => p.code === route.productCode)) {
              const prVal = entry.pr || route.pr
              line.products.push({
                code: route.productCode,
                name: route.productName,
                rate: parseFloat(entry.rate) || 0,
                pr: parseFloat(String(prVal)?.replace('%', '')) / 100 || 0,
              })
            }
          }
        }

        // Process cleaning mapping
        for (const cm of cleaningMapping) {
          if (!cm.cip) continue
          if (!cipMap.has(cm.cip)) {
            const dt = downtimeLookup.get(cm.cip)
            cipMap.set(cm.cip, {
              name: cm.cip,
              assets: [],
              downtime: dt?.downTime || null,
            })
          }
          const cip = cipMap.get(cm.cip)!
          if (!cip.assets.includes(cm.asset)) {
            cip.assets.push(cm.asset)
          }
        }

        // Build sync group index by line
        const syncCountByLine = new Map<string, number>()
        for (const sg of synchronizedLines) {
          if (sg.lines) {
            for (const l of sg.lines.split(',').map((s: string) => s.trim())) {
              syncCountByLine.set(l, (syncCountByLine.get(l) || 0) + 1)
            }
          }
        }

        // Populate sync counts on lines
        for (const [name, l] of lineMap) {
          l.syncGroupCount = syncCountByLine.get(name) || 0
        }

        // Build schedule indexes by kitchen/line. In multi-week consolidated
        // mode (consolidatedWeeks.length >= 2) the per-week Delta carries
        // ABSOLUTE consolidated-horizon times, and scheduler_api buckets a
        // spanning row into every week it touches. We subtract weekBaseHour
        // and clip to [0, 168] so the schedule accordion shows the current
        // week's slice (e.g. a kitchen run at absolute 140–210 in weeks 1–2
        // renders as 140h–168h in week 1's panel and 0h–42h in week 2's
        // panel). Rows whose per-resource activity falls entirely outside
        // the current week's slice are dropped from that resource — a row
        // bucketed here because of line overlap won't appear in the kitchen
        // accordion when the kitchen activity ended before this week.
        const WEEK_HOURS = 168
        // Week 1 of a consolidated run has offset 0 but still needs clipping
        // at hour 168 — a run spanning into week 2 would otherwise be counted
        // in full here as well as in week 2. Hence the separate flag rather
        // than testing `weekBaseHour === 0`.
        const isConsolidated =
          !!consolidatedWeeks && consolidatedWeeks.length >= 2
        const weekBaseHour = isConsolidated
          ? (selectedWeek - consolidatedWeeks[0]) * WEEK_HOURS
          : 0
        const TIME_FIELDS = [
          'kitchen_start_time',
          'kitchen_end_time',
          'line_start_time',
          'line_end_time',
          'kitchen_clean_start_time',
          'kitchen_clean_end_time',
          'kitchen_preclean_start_time',
          'kitchen_preclean_end_time',
          'line_clean_start_time',
          'line_clean_end_time',
          'line_preclean_start_time',
          'line_preclean_end_time',
          // Scalar changeovers count too — see ClipToWeek.ts.
          'kitchen_changeover_start_time',
          'kitchen_changeover_end_time',
          'line_changeover_start_time',
          'line_changeover_end_time',
        ] as const
        const clipNum = (v: unknown): unknown => {
          if (typeof v !== 'number') return v
          return Math.max(0, Math.min(WEEK_HOURS, v - weekBaseHour))
        }
        const overlapsWeek = (start: unknown, end: unknown): boolean => {
          if (typeof start !== 'number' || typeof end !== 'number') return true
          const relS = start - weekBaseHour
          const relE = end - weekBaseHour
          return relE > 0 && relS < WEEK_HOURS
        }
        const clipRow = (row: InputRow): InputRow => {
          if (!isConsolidated) return row
          const rec = row as unknown as Record<string, unknown>
          const out: Record<string, unknown> = { ...rec }
          for (const f of TIME_FIELDS) {
            if (typeof rec[f] === 'number') {
              out[f] = clipNum(rec[f])
            }
          }
          return out as unknown as InputRow
        }

        const scheduleByKitchen = new Map<string, InputRow[]>()
        const scheduleByLine = new Map<string, InputRow[]>()
        for (const row of outputData) {
          if (
            row.kitchen &&
            overlapsWeek(row.kitchen_start_time, row.kitchen_end_time)
          ) {
            const arr = scheduleByKitchen.get(row.kitchen) || []
            arr.push(clipRow(row))
            scheduleByKitchen.set(row.kitchen, arr)
          }
          if (
            row.line &&
            overlapsWeek(row.line_start_time, row.line_end_time)
          ) {
            const arr = scheduleByLine.get(row.line) || []
            arr.push(clipRow(row))
            scheduleByLine.set(row.line, arr)
          }
        }

        return {
          kitchens: kitchenMap,
          lines: lineMap,
          cips: cipMap,
          scheduleByKitchen,
          scheduleByLine,
        }
      }, [
        packagingRoutes,
        downtimeData,
        cleaningMapping,
        synchronizedLines,
        outputData,
        consolidatedWeeks,
        selectedWeek,
      ])

    // Build React Flow nodes and edges, grouped by site
    const { initialNodes, initialEdges } = useMemo(() => {
      const nodes: Node[] = []
      const edges: Edge[] = []

      const cipArr = Array.from(cips.values())

      // ── Determine site for each kitchen ──
      // Kitchens don't have a direct site — derive from connected lines
      const kitchenToSite = new Map<string, string>()
      // Also check if the kitchen itself has a downtime entry with site_location
      const downtimeLookup = new Map<string, string>()
      for (const dt of downtimeData) {
        if (dt.site_location) downtimeLookup.set(dt.resource, dt.site_location)
      }

      // First: try direct downtime lookup for kitchens
      for (const k of kitchens.keys()) {
        const site = downtimeLookup.get(k)
        if (site) kitchenToSite.set(k, site)
      }

      // Second: derive from connected lines
      for (const route of packagingRoutes) {
        if (!route.kitchen || kitchenToSite.has(route.kitchen)) continue
        const lineNames = [
          route.packingLine1,
          route.packingLine2,
          route.packingLine3,
          route.packingLine4,
        ]
        for (const ln of lineNames) {
          if (!ln) continue
          const lineData = lines.get(ln)
          if (lineData?.site) {
            kitchenToSite.set(route.kitchen, lineData.site)
            break
          }
        }
      }

      // ── Group by site ──
      const siteKitchens = new Map<string, string[]>()
      const siteLines = new Map<string, string[]>()

      for (const [kName] of kitchens) {
        const site = kitchenToSite.get(kName) || 'Unassigned'
        if (!siteKitchens.has(site)) siteKitchens.set(site, [])
        siteKitchens.get(site)!.push(kName)
      }

      for (const [lName, lData] of lines) {
        const site = lData.site || 'Unassigned'
        if (!siteLines.has(site)) siteLines.set(site, [])
        siteLines.get(site)!.push(lName)
      }

      // Collect all site names (union of kitchen and line sites)
      const allSites = Array.from(
        new Set([...siteKitchens.keys(), ...siteLines.keys()])
      )

      // ── Layout constants ──
      const NODE_H = 80
      const Y_SPACING = 160
      const SITE_PAD_TOP = 60
      const SITE_PAD_BOTTOM = 40
      const SITE_PAD_X = 40
      const KITCHEN_X = SITE_PAD_X
      const LINE_X = 500
      const SITE_GAP = 80
      const SITE_WIDTH = 900
      const CIP_X = SITE_WIDTH + SITE_GAP + 100

      // Track absolute positions of lines for sync group containers
      const lineAbsolutePositions = new Map<string, { x: number; y: number }>()

      let siteYOffset = 0

      for (const site of allSites) {
        const sKitchens = siteKitchens.get(site) || []
        const sLines = siteLines.get(site) || []
        const maxItems = Math.max(sKitchens.length, sLines.length, 1)
        const siteHeight = SITE_PAD_TOP + maxItems * Y_SPACING + SITE_PAD_BOTTOM

        // Site container node
        const siteId = `site-${site}`
        nodes.push({
          id: siteId,
          type: 'site',
          position: { x: 0, y: siteYOffset },
          data: { label: site, width: SITE_WIDTH, height: siteHeight },
          style: { width: SITE_WIDTH, height: siteHeight, zIndex: -1 },
          draggable: true,
          selectable: false,
        })

        // Kitchen nodes inside site
        sKitchens.forEach((kName, i) => {
          const k = kitchens.get(kName)!
          nodes.push({
            id: `kitchen-${kName}`,
            type: 'kitchen',
            position: { x: KITCHEN_X, y: SITE_PAD_TOP + i * Y_SPACING },
            data: {
              ...k,
              onSelect,
              selected: false,
              scheduleRows: scheduleByKitchen.get(kName) || [],
            },
            parentId: siteId,
          })
        })

        // Line nodes inside site
        sLines.forEach((lName, i) => {
          const l = lines.get(lName)!
          const relX = LINE_X
          const relY = SITE_PAD_TOP + i * Y_SPACING
          lineAbsolutePositions.set(lName, { x: relX, y: siteYOffset + relY })
          nodes.push({
            id: `line-${lName}`,
            type: 'line',
            position: { x: relX, y: relY },
            data: {
              ...l,
              onSelect,
              selected: false,
              scheduleRows: scheduleByLine.get(lName) || [],
            },
            parentId: siteId,
          })
        })

        siteYOffset += siteHeight + SITE_GAP
      }

      // CIP nodes — positioned outside site containers on the right
      cipArr.forEach((c, i) => {
        nodes.push({
          id: `cip-${c.name}`,
          type: 'cip',
          position: { x: CIP_X, y: i * Y_SPACING },
          data: { ...c, onSelect, selected: false },
        })
      })

      // ── Sync group containers ──
      const NODE_W = 200 // approximate line node width
      const GROUP_PAD = 20
      for (const sg of synchronizedLines) {
        if (!sg.lines || !sg.groupName) continue
        const memberLines = sg.lines
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean)
        const positions = memberLines
          .map((l) => lineAbsolutePositions.get(l))
          .filter(Boolean) as { x: number; y: number }[]
        if (positions.length < 2) continue

        const minX = Math.min(...positions.map((p) => p.x)) - GROUP_PAD
        const minY = Math.min(...positions.map((p) => p.y)) - GROUP_PAD
        const maxX = Math.max(...positions.map((p) => p.x)) + NODE_W + GROUP_PAD
        const maxY = Math.max(...positions.map((p) => p.y)) + NODE_H + GROUP_PAD
        const groupType =
          sg.productMasterType === 'Active' ? 'Active' : 'temperature'

        nodes.push({
          id: `syncgroup-${sg.groupName}`,
          type: 'syncGroup',
          position: { x: minX, y: minY },
          data: {
            label: sg.groupName,
            type: groupType,
            width: maxX - minX,
            height: maxY - minY,
          },
          style: { width: maxX - minX, height: maxY - minY, zIndex: -2 },
          draggable: false,
          selectable: false,
        })
      }

      // ── Edges: kitchen → line ──
      // Build set of active kitchen→line pairs from schedule output
      const activeEdges = new Set<string>()
      for (const row of outputData) {
        if (row.kitchen && row.line) {
          activeEdges.add(`${row.kitchen}->${row.line}`)
        }
      }
      const hasSchedule = activeEdges.size > 0

      const edgeSet = new Set<string>()
      for (const route of packagingRoutes) {
        const lineEntries = [
          route.packingLine1,
          route.packingLine2,
          route.packingLine3,
          route.packingLine4,
        ]
        for (const lineName of lineEntries) {
          if (!lineName) continue
          const edgeKey = `${route.kitchen}->${lineName}`
          if (edgeSet.has(edgeKey)) continue
          edgeSet.add(edgeKey)
          const isActive = activeEdges.has(edgeKey)
          edges.push({
            id: edgeKey,
            source: `kitchen-${route.kitchen}`,
            target: `line-${lineName}`,
            style: hasSchedule
              ? {
                  stroke: isActive ? '#1976d2' : '#bdbdbd',
                  strokeWidth: isActive ? 2.5 : 1,
                  strokeDasharray: isActive ? undefined : '6,4',
                }
              : { stroke: '#1976d2', strokeWidth: 2 },
          })
        }
      }

      return { initialNodes: nodes, initialEdges: edges }
    }, [
      kitchens,
      lines,
      cips,
      packagingRoutes,
      downtimeData,
      cleaningMapping,
      synchronizedLines,
      outputData,
      onSelect,
    ])

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)

    // Sync when data changes, preserving current selection
    useEffect(() => {
      const selectedId = selectionRef.current
      if (selectedId) {
        setNodes(
          initialNodes.map((n) =>
            n.id === selectedId
              ? { ...n, data: { ...n.data, selected: true } }
              : n
          )
        )
      } else {
        setNodes(initialNodes)
      }
      setEdges(initialEdges)
    }, [initialNodes, initialEdges, setNodes, setEdges])

    const flowArea = (
      <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
        {focusedItem ? (
          <ProductFlowDiagram
            focusedItem={focusedItem}
            packagingRoutes={packagingRoutes}
            productionRequirements={productionRequirements}
            downtimeData={downtimeData}
            allLines={lines}
            onSaveData={onSaveData}
            selectedWeek={selectedWeek}
            onBack={() => setFocusedItem(null)}
          />
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onPaneClick={onDeselect}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2, minZoom: 0.8 }}
            minZoom={0.3}
            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
            proOptions={{ hideAttribution: true }}
            nodesConnectable={false}
            nodesFocusable={false}
            edgesFocusable={false}
            elementsSelectable={false}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={16}
              size={1}
              color="#e0e0e0"
            />
            <Controls />
          </ReactFlow>
        )}
      </Box>
    )

    if (hideSidePanel) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          {flowArea}
        </Box>
      )
    }

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 400,
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        {flowArea}
        <ScheduleControlPanel
          schedulerId={schedulerId}
          iteration={iteration}
          selectedWeek={selectedWeek}
          onWeekChange={onWeekChange}
          outputRows={outputData}
          dataSummary={dataSummary}
          pendingComment={pendingComment}
          onClearPendingComment={onClearPendingComment}
          notProducedIds={notProducedIds}
          onResetNotProduced={onResetNotProduced}
        />
      </Box>
    )
  }
)
